import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import exifr from "exifr";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import VoiceNoteButton from "@/components/VoiceNoteButton";
import {
  Camera, Sparkle, MapPin, X, CheckCircle, CaretDown, ArrowRight,
  Notebook, Question, WarningCircle, Article, LinkSimple, Globe, Plus,
  UploadSimple,
} from "@phosphor-icons/react";

/**
 * AddSightingSimple — lean, single-scroll, white-card submission for the
 * NaturePulse Observation Network (v2).
 *
 * TWO PARALLEL PATHS from the same card:
 *  1. Take a picture OR upload one → runs Gemini vision, EXIF, geo-lookup,
 *     posts to /nature/observations.
 *  2. Paste any URL → runs /nature/stories/analyze, previews the extracted
 *     headline/summary/thumbnail/tags/category; the user can edit, add
 *     supplementary photos, and publish as a `news_link` story via
 *     /nature/stories/create-story.
 *
 * Both flows share the same submitter identity + location resolution.
 */

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

// Haversine in metres — used only to decide whether to show the conflict picker.
const distanceMeters = (a, b) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const isValidHttpUrl = (v) => {
  try { const u = new URL(v.trim()); return u.protocol === "http:" || u.protocol === "https:"; }
  catch { return false; }
};

const MAX_PHOTOS = 6;

const empty = {
  title: "", description: "",
  category: "wildlife",
  country: "", state: "", city: "", county: "", village: "", locality: "",
  lat: "", lon: "",
  tags: [], notes: "",
  guest_name: "", guest_email: "",
  date: "", time: "",
  photoTimestamp: null,
};

export default function AddSightingSimple() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const extraFileRef = useRef(null);
  const [photos, setPhotos] = useState([]); // [{id, dataUrl, name}] — up to MAX_PHOTOS, all equal
  const [form, setForm] = useState(empty);
  const [locResolving, setLocResolving] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [published, setPublished] = useState(null);
  const [needsId, setNeedsId] = useState(false);
  const [locConflict, setLocConflict] = useState(null);
  const [categories, setCategories] = useState([]);

  // ---- Link-expansion path ----
  const [urlInput, setUrlInput] = useState("");
  const [expanding, setExpanding] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null); // {url, canonical_url, headline, summary, category, tags, publisher, source_domain, image, location, extraction_confidence}
  const [extraMedia, setExtraMedia] = useState([]); // supplementary uploaded photos [{url, media_type}]
  const [uploadingExtra, setUploadingExtra] = useState(false);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));  const patchPreview = (k, v) => setLinkPreview((p) => ({ ...(p || {}), [k]: v }));

  // Load hierarchical categories from the backend once — used for the dropdown.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get("/nature/categories", { params: { as_tree: false } });
        if (!alive) return;
        const all = Array.isArray(data?.categories) ? data.categories : [];
        const parentSlugs = new Set(all.map((c) => c.parent).filter(Boolean));
        const leaves = all.filter((c) => !parentSlugs.has(c.slug));
        const sorted = (leaves.length ? leaves : all).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        // The default category ("wildlife") is a parent, so it isn't in the
        // leaf-only list — prepend it so the <select> never displays a
        // misleading first-option label while state holds a different slug.
        if (!sorted.some((c) => c.slug === "wildlife")) {
          const wc = all.find((c) => c.slug === "wildlife");
          sorted.unshift(wc || { slug: "wildlife", label: "Wildlife (general)", emoji: "🦋" });
        }
        setCategories(sorted);
      } catch {
        setCategories([
          { slug: "wildlife", label: "Wildlife", emoji: "🦋" },
          { slug: "plants", label: "Plants", emoji: "🌿" },
          { slug: "birds", label: "Birds", emoji: "🐦" },
          { slug: "insects", label: "Insects", emoji: "🦗" },
          { slug: "trees", label: "Trees", emoji: "🌳" },
        ]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // -------- Photo pick (multi, up to MAX_PHOTOS) + EXIF prefill --------
  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) return toast.error(`Up to ${MAX_PHOTOS} photos per sighting`);
    if (files.length > room) toast.message(`Max ${MAX_PHOTOS} photos — kept the first ${room}`);

    const added = [];
    for (const file of files.slice(0, room)) {
      if (file.size > 12 * 1024 * 1024) { toast.error(`${file.name} too large (12 MB max)`); continue; }
      added.push({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        dataUrl: await readAsDataURL(file),
        file,
        name: file.name,
      });
    }
    if (!added.length) return;

    const firstPhotoOfSighting = photos.length === 0;
    setPhotos((p) => [...p, ...added]);

    if (firstPhotoOfSighting) {
      // Switching into photo mode — reset the link path.
      setLinkPreview(null);
      setUrlInput("");
      setExtraMedia([]);
      setLocConflict(null);

      // EXIF GPS + DateTimeOriginal from the FIRST photo — local, fast,
      // non-blocking. AI species ID happens server-side after publishing.
      (async () => {
        try {
          const meta = await exifr.parse(added[0].file, { gps: true, tiff: true, exif: true });
          const gps = (meta?.latitude != null && meta?.longitude != null)
            ? { lat: meta.latitude, lon: meta.longitude } : null;
          const shotAt = meta?.DateTimeOriginal || meta?.CreateDate;
          if (shotAt instanceof Date && !isNaN(shotAt.getTime())) {
            patch("date", shotAt.toISOString().slice(0, 10));
            patch("time", shotAt.toTimeString().slice(0, 5));
            patch("photoTimestamp", shotAt.toISOString());
          }
          if (gps) {
            patch("lat", gps.lat.toFixed(6));
            patch("lon", gps.lon.toFixed(6));
            resolveLocation(gps.lat, gps.lon);
            checkLocationConflict(gps);
          }
        } catch { /* no EXIF, no problem */ }
      })();
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((ph) => ph.id !== id));

  const resolveLocation = async (lat, lon) => {
    setLocResolving(true);
    try {
      const { data } = await http.post("/nature/geo/lookup", { lat, lon });
      patch("country", data?.country || "");
      patch("state", data?.state || "");
      patch("county", data?.county || "");
      patch("city", data?.city || "");
      patch("village", data?.village || "");
      patch("locality", data?.locality || "");
    } catch { /* keep raw coords if the geocoder fails */ }
    finally { setLocResolving(false); }
  };

  const checkLocationConflict = (photo) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const device = { lat: pos.coords.latitude, lon: pos.coords.longitude };
      const d = distanceMeters(photo, device);
      if (d > 500) setLocConflict({ photo, device, distance_m: Math.round(d) });
    }, () => { /* ignore — user didn't grant */ }, { enableHighAccuracy: false, timeout: 8000 });
  };

  const chooseLocation = (which) => {
    if (!locConflict) return;
    const p = which === "photo" ? locConflict.photo : locConflict.device;
    patch("lat", p.lat.toFixed(6));
    patch("lon", p.lon.toFixed(6));
    resolveLocation(p.lat, p.lon);
    setLocConflict(null);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition((pos) => {
      patch("lat", pos.coords.latitude.toFixed(6));
      patch("lon", pos.coords.longitude.toFixed(6));
      resolveLocation(pos.coords.latitude, pos.coords.longitude);
    }, () => toast.error("Permission denied"), { enableHighAccuracy: true, timeout: 12000 });
  };

  const clear = () => {
    setPhotos([]); setForm(empty); setPublished(null); setNeedsId(false);
    setShowMore(false); setLocConflict(null);
    setLinkPreview(null); setUrlInput(""); setExtraMedia([]);
  };

  // -------- URL / Link expansion path --------
  const expandUrl = async () => {
    const v = urlInput.trim();
    if (!v) return toast.error("Paste a link first");
    if (!isValidHttpUrl(v)) return toast.error("That doesn't look like a valid URL");
    if (!user) return toast.error("Sign in to explore a link");

    setExpanding(true);
    try {
      const { data } = await http.post("/nature/stories/analyze", { url: v });
      const meta = data?.meta || data;
      // Prefer a category slug that actually exists in the loaded dropdown
      // options; fall back to `wildlife` (a safe, universally-present slug) so
      // the <select> doesn't silently default to the first alphabetical option
      // ("aftershock") which would misfile the story.
      const rawCat = (data?.category || meta?.category || "").toString().toLowerCase();
      const knownSlugs = new Set(categories.map((c) => c.slug));
      const safeCat = rawCat && knownSlugs.has(rawCat) ? rawCat : (knownSlugs.has("wildlife") ? "wildlife" : (categories[0]?.slug || "science"));
      const preview = {
        url: v,
        canonical_url: data?.canonical_url || v,
        headline: (meta?.headline || data?.headline || data?.title || "").slice(0, 200),
        summary: (meta?.summary || data?.summary || "").slice(0, 800),
        category: safeCat,
        tags: Array.isArray(data?.tags) ? data.tags.slice(0, 8) : Array.isArray(meta?.tags) ? meta.tags.slice(0, 8) : [],
        publisher: data?.publisher || meta?.publisher || data?.source_domain || "",
        source_domain: data?.source_domain || "",
        image: data?.image || meta?.image || data?.thumbnail || "",
        location: data?.location || meta?.location || null,
        extraction_confidence: typeof data?.extraction_confidence === "number" ? data.extraction_confidence : null,
      };
      setLinkPreview(preview);
      // Reset photo path if user switches
      setPhotos([]);
      setForm(empty);
      // Pull location off the extraction if we don't have one yet
      if (preview.location && !form.country) {
        patch("country", preview.location.country || "");
        patch("state", preview.location.state || "");
        patch("city", preview.location.city || "");
      }
      toast.success("Link expanded — edit anything, then post.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not expand link — try again or paste a different URL.";
      toast.error(typeof msg === "string" ? msg : "Could not expand link");
    } finally { setExpanding(false); }
  };

  const pickExtraPhotos = () => extraFileRef.current?.click();

  const onExtraFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!user) return toast.error("Sign in to add photos");
    setUploadingExtra(true);
    try {
      for (const file of files.slice(0, 6)) {
        if (file.size > 12 * 1024 * 1024) { toast.error(`${file.name} too large (12 MB max)`); continue; }
        const url = await readAsDataURL(file);
        const { data: up } = await http.post("/uploads/photo", { image_base64: url });
        setExtraMedia((m) => [...m, { url: up.url, media_type: "image", caption: "" }]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally { setUploadingExtra(false); }
  };

  const removeExtraMedia = (i) => setExtraMedia((m) => m.filter((_, idx) => idx !== i));

  // ---- Submit the LINK story (news_link) ----
  const submitLink = async () => {
    if (!linkPreview) return;
    if (!user) return toast.error("Sign in to post");
    const headline = (linkPreview.headline || "").trim();
    if (!headline) return toast.error("Give the link a short headline");
    setSubmitting(true);
    try {
      const payload = {
        story_type: "news_link",
        title: headline,
        body: (linkPreview.summary || "").trim() || undefined,
        category: linkPreview.category || undefined,
        tags: (linkPreview.tags || []).map((t) => (typeof t === "string" ? t.trim() : "")).filter(Boolean),
        location: {
          country: form.country || linkPreview.location?.country || undefined,
          state: form.state || linkPreview.location?.state || undefined,
          city: form.city || linkPreview.location?.city || undefined,
          lat: form.lat ? parseFloat(form.lat) : undefined,
          lon: form.lon ? parseFloat(form.lon) : undefined,
          scope: "country",
          privacy: "exact",
        },
        media: extraMedia,
        links: [{ url: linkPreview.url, link_type: "webpage", note: linkPreview.publisher || undefined }],
      };
      const { data } = await http.post("/nature/stories/create-story", payload);
      setPublished({
        ...data,
        title: data.title || headline,
      });
      toast.success(data.moderation_status === "approved" ? "Posted." : "Submitted for review.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) toast.error("Sign in to post.");
      else toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally { setSubmitting(false); }
  };

  // ---- Submit the PHOTO sighting (multi-photo, background AI) ----
  const submit = async () => {
    if (!photos.length) return toast.error("Add a photo first");
    if (!form.title.trim()) return toast.error("Give it a short title");
    if (!form.city && !form.state && !form.country && !form.lat) {
      return toast.error("Add a location or use My location");
    }
    if (!user) {
      return toast.error("Sign in to submit a sighting");
    }
    setSubmitting(true);
    try {
      // Upload every photo (up to 6) — all equal, swipeable gallery.
      const media = [];
      for (const p of photos) {
        const { data: up } = await http.post("/uploads/photo", { image_base64: p.dataUrl });
        media.push({ url: up.url, kind: "image" });
      }
      const payload = {
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim() || null,
        media,
        ai_pending: true, // AI species/tag ID runs server-side after publish
        date: form.date || null,
        time: form.time || null,
        location: {
          lat: form.lat ? parseFloat(form.lat) : null,
          lon: form.lon ? parseFloat(form.lon) : null,
          precision: "exact",
          country: form.country || null,
          state: form.state || null,
          county: form.county || null,
          city: form.city || null,
          village: form.village || null,
          locality: form.locality || null,
        },
        tags: form.tags,
        notes: form.notes.trim() || null,
        needs_identification: needsId || undefined,
        original_photo_timestamp: form.photoTimestamp || null,
      };
      const { data } = await http.post("/nature/observations", payload);
      setPublished(data);
      toast.success(data.moderation_status === "approved"
        ? "Sighting published — AI identification running in the background."
        : "Submitted for review.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) toast.error("Sign in to submit a sighting.");
      else toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally { setSubmitting(false); }
  };

  // -------- Success screen (shared) --------
  if (published) {
    const publishedIsLink = published.story_type === "news_link";
    const detailHref = publishedIsLink ? `/stories/${published.id}` : `/observations/${published.id}`;
    const isApproved = published.moderation_status === "approved";
    const rawId = String(published.id || "");
    const refCode = `NP-${(publishedIsLink ? "S" : "O")}-${rawId.replace(/^(obs|story)_/, "").slice(0, 8).toUpperCase()}`;
    const copyRef = async () => {
      try {
        await navigator.clipboard.writeText(`${refCode} · ${window.location.origin}${detailHref}`);
        toast.success("Reference copied");
      } catch { toast.error("Couldn't copy"); }
    };
    return (
      <section id="identify" data-testid="add-sighting-section" className="py-16 md:py-24 px-5 md:px-10">
        <div className="max-w-[720px] mx-auto rounded-2xl bg-white text-[#1B4332] border border-black/10 p-8 md:p-10 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3">
            <CheckCircle size={26} weight="fill" className="text-emerald-600" />
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            {isApproved ? "Published" : "Submitted for review"}
          </div>
          <h2 className="mt-1 font-serif-display text-3xl leading-tight">{published.title}</h2>
          {published.description && (
            <p className="mt-2 text-sm text-[#3A3830] line-clamp-4">{published.description}</p>
          )}
          {published.ai_pending && (
            <div
              data-testid="add-sighting-ai-pending"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase"
            >
              <Sparkle size={12} weight="fill" className="animate-pulse" />
              AI is identifying species & details in the background
            </div>
          )}

          {/* Submission receipt — reference number + moderation status */}
          <div className="mt-5 rounded-xl border border-black/10 bg-[color:var(--np-bg,#F5EBDC)]/50 p-4" data-testid="submission-receipt">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
              Submission receipt
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="font-mono-tech text-base tracking-[0.14em] text-[#1B4332]" data-testid="receipt-ref">{refCode}</code>
              <button
                onClick={copyRef}
                data-testid="receipt-copy"
                className="rounded-full border border-black/15 px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
              >Copy</button>
              <span
                data-testid="receipt-status"
                className={`inline-flex items-center rounded-full px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase border ${
                  isApproved
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
              >
                {isApproved ? "Live now" : "In review · usually under 24 h"}
              </span>
            </div>
            <p className="mt-2 text-xs text-[#3A3830]">
              Save this reference — quote it if you ever ask us about this submission. Your {publishedIsLink ? "story" : "sighting"} link below works even while it's in review.
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={clear} data-testid="add-sighting-again" className="inline-flex items-center gap-1 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
              Post another <ArrowRight size={12} weight="bold" />
            </button>
            <a
              href={detailHref}
              data-testid="add-sighting-view"
              className="inline-flex items-center gap-1 rounded-full border border-black/15 text-[#1B4332] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              View {publishedIsLink ? "story" : "sighting"}
            </a>
          </div>
        </div>
      </section>
    );
  }

  const locationSummary = locResolving
    ? "Resolving location…"
    : [form.city || form.village, form.state, form.country].filter(Boolean).join(", ") || "No location yet";

  // Determine which "mode" the user is in
  const photoMode = photos.length > 0;
  const linkMode = Boolean(linkPreview) && !photoMode;
  const emptyMode = !linkMode && !photoMode;

  return (
    <section id="identify" data-testid="add-sighting-section" className="py-16 md:py-24 px-5 md:px-10">
      <div className={`mx-auto ${(photoMode || linkPreview) ? "max-w-[720px]" : "max-w-[1200px]"}`}>
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
          / Share what you saw — or what you know
        </div>
        <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.02] tracking-tight text-[#1B4332]">
          Add a sighting, or tell a story.
        </h2>
        <p className="mt-2 text-[#3A3830] text-sm md:text-base max-w-2xl">
          Take a photo, upload one from your library, paste a link, share a memory, ask a question — every observation matters.
        </p>

        <div className={`mt-6 grid gap-4 md:gap-5 ${(photoMode || linkPreview) ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[1.15fr_0.85fr]"}`}>
        <div className="rounded-2xl bg-white text-[#1B4332] border border-black/10 p-5 md:p-6 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 grid place-items-center text-[color:var(--np-brand,#B85F2A)]">
              <Camera size={16} weight="fill" />
            </div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
              {photoMode ? `${photos.length} photo${photos.length > 1 ? "s" : ""} · up to ${MAX_PHOTOS}` : linkMode ? "Link expanded · edit and post" : "Two ways to share"}
            </div>
          </div>

          {/* ============================ EMPTY MODE ============================ */}
          {emptyMode && (
            <>
              {/* Big picker — no `capture` attribute so iOS shows the full sheet (Take Photo · Photo Library · Choose File) */}
              <button
                onClick={pick}
                data-testid="add-sighting-pick"
                className="w-full rounded-xl border-2 border-dashed border-black/25 bg-[color:var(--np-bg,#F5EBDC)]/60 hover:bg-[color:var(--np-bg,#F5EBDC)] p-8 md:p-10 flex flex-col items-center gap-2 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Camera size={30} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                  <div className="w-px h-8 bg-black/15" />
                  <UploadSimple size={28} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
                </div>
                <div className="font-serif-display text-xl md:text-2xl">Take a picture or upload up to {MAX_PHOTOS}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">JPG · PNG · WEBP · up to 12 MB each · AI identifies species after you post</div>
              </button>

              {/* OR divider */}
              <div className="my-5 flex items-center gap-3" aria-hidden="true">
                <div className="flex-1 h-px bg-black/10" />
                <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted,#3A3830)]">or</span>
                <div className="flex-1 h-px bg-black/10" />
              </div>

              {/* Link expansion input */}
              <div className="rounded-xl border border-black/15 bg-white p-4 md:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <LinkSimple size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">
                    Paste a link — we'll explore it and show you the context
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !expanding) { e.preventDefault(); expandUrl(); } }}
                    placeholder="https://example.com/article-or-video"
                    data-testid="add-sighting-url-input"
                    className="flex-1 rounded-lg border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                  />
                  <button
                    onClick={expandUrl}
                    disabled={expanding || !urlInput.trim()}
                    data-testid="add-sighting-url-expand"
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50 hover:bg-[#a25526]"
                  >
                    {expanding ? (
                      <><Sparkle size={13} weight="fill" className="animate-pulse" /> Exploring…</>
                    ) : (
                      <><Globe size={13} weight="fill" /> Explore link</>
                    )}
                  </button>
                </div>
                <p className="mt-2 font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[color:var(--np-muted,#3A3830)]">
                  News article · YouTube · research paper · report
                </p>
              </div>
            </>
          )}

          {/* ============================ PHOTO MODE (multi) ============================ */}
          {photoMode && (
            <div>
              <div className="grid grid-cols-3 gap-2" data-testid="add-sighting-photo-grid">
                {photos.map((p, i) => (
                  <div key={p.id} className="relative rounded-xl overflow-hidden bg-black/5 aspect-square" data-testid={`add-sighting-photo-${i}`}>
                    <img src={p.dataUrl} alt={p.name || `Photo ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(p.id)}
                      data-testid={`add-sighting-photo-remove-${i}`}
                      className="absolute top-1.5 right-1.5 rounded-full bg-black/60 text-white p-1.5"
                      aria-label={`Remove photo ${i + 1}`}
                    ><X size={12} weight="bold" /></button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={pick}
                    data-testid="add-sighting-add-photo"
                    className="aspect-square rounded-xl border-2 border-dashed border-black/25 bg-[color:var(--np-bg,#F5EBDC)]/60 hover:bg-[color:var(--np-bg,#F5EBDC)] flex flex-col items-center justify-center gap-1 transition-colors"
                  >
                    <Plus size={18} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
                    <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-muted,#3A3830)]">
                      Add · {photos.length}/{MAX_PHOTOS}
                    </span>
                  </button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <div
                  data-testid="add-sighting-ai-note"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase"
                >
                  <Sparkle size={11} weight="fill" /> AI suggests species after you post
                </div>
                <button
                  type="button"
                  onClick={clear}
                  data-testid="add-sighting-clear"
                  className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#1B4332]"
                >Remove all</button>
              </div>
            </div>
          )}

          {/* Hidden input — NO `capture` attribute; iOS/Android show the full sheet with camera + library options. Multi-select enabled. */}
          <input
            ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={onFile} className="hidden"
            data-testid="add-sighting-input"
          />

          {/* Location conflict picker (>0.5 km) */}
          {photoMode && locConflict && (
            <div
              data-testid="add-sighting-conflict"
              className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-[#1B4332]"
            >
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-amber-700 mb-1">
                Location conflict · {locConflict.distance_m} m apart
              </div>
              <p className="text-xs text-[#3A3830] mb-2">
                Your photo and your device disagree on where you are. Which one is right?
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => chooseLocation("photo")}
                  data-testid="add-sighting-conflict-photo"
                  className="rounded-full bg-white border border-black/15 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase"
                >Use photo GPS</button>
                <button
                  onClick={() => chooseLocation("device")}
                  data-testid="add-sighting-conflict-device"
                  className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase"
                >Use device GPS</button>
              </div>
            </div>
          )}

          {/* -------- PHOTO detail form (existing flow) -------- */}
          {photoMode && (
            <div className="mt-5 space-y-4">
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">What is it?</label>
                <input
                  value={form.title}
                  onChange={(e) => patch("title", e.target.value)}
                  placeholder="Purple sunbird on a hibiscus"
                  data-testid="add-sighting-title"
                  className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-base focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                />
                <button
                  type="button"
                  onClick={() => setNeedsId((v) => !v)}
                  data-testid="add-sighting-needs-id"
                  aria-pressed={needsId}
                  className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase transition-colors ${
                    needsId
                      ? "bg-[#1B4332] text-[#F5EBDC] border-transparent"
                      : "border-black/15 text-[color:var(--np-muted,#3A3830)] hover:bg-black/5"
                  }`}
                >
                  <Question size={12} weight="bold" />
                  {needsId ? "Community will help identify this ✓" : "I don't know what this is — ask the community"}
                </button>
                {needsId && (
                  <p className="mt-1.5 text-xs text-[#3A3830]" data-testid="add-sighting-needs-id-note">
                    No problem — post it anyway. AI will take a first guess, and other members can suggest an identification.
                  </p>
                )}
              </div>

              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] flex items-center gap-1">
                  <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Where?
                </label>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[220px] rounded-lg bg-white border border-black/15 px-3 py-2 text-sm">
                    {locationSummary}
                  </div>
                  <button
                    type="button"
                    onClick={useMyLocation}
                    data-testid="add-sighting-use-my-location"
                    className="inline-flex items-center gap-1 rounded-lg border border-black/15 px-3 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#1B4332] hover:bg-[color:var(--np-bg,#F5EBDC)]"
                  >
                    <MapPin size={12} weight="fill" /> Use my location
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowMore((v) => !v)}
                  data-testid="add-sighting-more-toggle"
                  className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
                >
                  <CaretDown size={12} weight="bold" className={`transition-transform ${showMore ? "rotate-180" : ""}`} />
                  {showMore ? "Hide details" : "More details"}
                </button>
                {showMore && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Description · tell the full story</label>
                        <VoiceNoteButton
                          testid="add-sighting-voice"
                          onTranscript={(text) => setForm((f) => ({ ...f, description: (f.description ? f.description.trimEnd() + " " : "") + text }))}
                        />
                      </div>
                      <textarea
                        value={form.description}
                        onChange={(e) => patch("description", e.target.value)}
                        rows={5}
                        placeholder="What happened, when, what you noticed — as long as you need. Great for disasters, accidents and multi-photo reports…"
                        data-testid="add-sighting-description"
                        className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm leading-relaxed"
                      />
                    </div>
                    <div>
                      <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Category</label>
                      <select
                        value={form.category}
                        onChange={(e) => patch("category", e.target.value)}
                        data-testid="add-sighting-category"
                        className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                      >
                        {categories.map((c) => (
                          <option key={c.slug} value={c.slug}>{c.emoji ? `${c.emoji} ` : ""}{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Tags</label>
                      <input
                        value={form.tags.join(", ")}
                        onChange={(e) => patch("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                        placeholder="wildlife, garden, morning"
                        data-testid="add-sighting-tags"
                        className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    {(form.date || form.time) && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Date</label>
                          <input value={form.date} onChange={(e) => patch("date", e.target.value)} type="date" data-testid="add-sighting-date" className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Time</label>
                          <input value={form.time} onChange={(e) => patch("time", e.target.value)} type="time" data-testid="add-sighting-time" className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-2 flex items-center gap-3">
                <button
                  disabled={submitting}
                  onClick={submit}
                  data-testid="add-sighting-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                >
                  <CheckCircle size={13} weight="fill" /> {submitting ? "Submitting…" : "Post sighting"}
                </button>
                <button
                  type="button"
                  onClick={clear}
                  className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#1B4332]"
                >Cancel</button>
              </div>
              {!user && (
                <div className="text-xs text-[#3A3830]">
                  Sign-in required — the Observation Network keeps every sighting attributable so followers and curators can act on it.
                </div>
              )}
            </div>
          )}

          {/* ============================ LINK MODE ============================ */}
          {linkMode && (
            <div className="space-y-4" data-testid="add-sighting-link-preview">
              {/* Preview header — thumbnail + domain */}
              <div className="rounded-xl overflow-hidden border border-black/10 bg-[color:var(--np-bg,#F5EBDC)]/50">
                {linkPreview.image ? (
                  <div className="relative">
                    <img
                      src={linkPreview.image}
                      alt=""
                      onError={(e) => { e.target.style.display = "none"; }}
                      className="w-full max-h-[240px] object-cover"
                    />
                    <button
                      onClick={clear}
                      data-testid="add-sighting-link-clear"
                      className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-1.5"
                      aria-label="Remove"
                    ><X size={14} weight="bold" /></button>
                  </div>
                ) : (
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Globe size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] flex-shrink-0" />
                      <div className="truncate text-sm text-[#3A3830]">{linkPreview.source_domain || linkPreview.url}</div>
                    </div>
                    <button
                      onClick={clear}
                      data-testid="add-sighting-link-clear"
                      className="rounded-full bg-black/10 hover:bg-black/20 text-[#1B4332] p-1.5 flex-shrink-0"
                      aria-label="Remove"
                    ><X size={14} weight="bold" /></button>
                  </div>
                )}
                <div className="px-4 py-2 text-[11px] font-mono-tech tracking-[0.2em] uppercase text-[color:var(--np-muted,#3A3830)] flex items-center gap-2 flex-wrap border-t border-black/5">
                  <LinkSimple size={11} weight="fill" />
                  <span className="truncate max-w-[300px]">{linkPreview.publisher || linkPreview.source_domain || linkPreview.url}</span>
                  {linkPreview.extraction_confidence != null && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[color:var(--np-brand,#B85F2A)]">
                      <Sparkle size={10} weight="fill" /> AI · {Math.round(linkPreview.extraction_confidence * 100)}%
                    </span>
                  )}
                </div>
              </div>

              {/* Editable headline */}
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Headline</label>
                <input
                  value={linkPreview.headline}
                  onChange={(e) => patchPreview("headline", e.target.value.slice(0, 200))}
                  placeholder="Headline"
                  data-testid="add-sighting-link-title"
                  className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-base focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
                />
              </div>

              {/* Editable summary */}
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Summary · what's this about?</label>
                <textarea
                  value={linkPreview.summary}
                  onChange={(e) => patchPreview("summary", e.target.value.slice(0, 800))}
                  rows={4}
                  placeholder="A short summary — you can edit what the AI pulled…"
                  data-testid="add-sighting-link-summary"
                  className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Category */}
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Category</label>
                <select
                  value={linkPreview.category || "science"}
                  onChange={(e) => patchPreview("category", e.target.value)}
                  data-testid="add-sighting-link-category"
                  className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                >
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.emoji ? `${c.emoji} ` : ""}{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Tags</label>
                <input
                  value={(linkPreview.tags || []).join(", ")}
                  onChange={(e) => patchPreview("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                  placeholder="climate, monsoon, kerala"
                  data-testid="add-sighting-link-tags"
                  className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Location — reuse the same location line as photo mode */}
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] flex items-center gap-1">
                  <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Where does this apply?
                </label>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[220px] rounded-lg bg-white border border-black/15 px-3 py-2 text-sm">
                    {locationSummary}
                  </div>
                  <button
                    type="button"
                    onClick={useMyLocation}
                    data-testid="add-sighting-link-use-my-location"
                    className="inline-flex items-center gap-1 rounded-lg border border-black/15 px-3 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#1B4332] hover:bg-[color:var(--np-bg,#F5EBDC)]"
                  >
                    <MapPin size={12} weight="fill" /> Use my location
                  </button>
                </div>
              </div>

              {/* Supplementary photos */}
              <div>
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Add photos (optional)</label>
                {extraMedia.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {extraMedia.map((m, i) => (
                      <div key={i} className="relative rounded-lg overflow-hidden bg-black/5 aspect-square">
                        <img src={m.url} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeExtraMedia(i)}
                          data-testid={`add-sighting-link-media-remove-${i}`}
                          className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1"
                          aria-label="Remove"
                        ><X size={10} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={pickExtraPhotos}
                  disabled={uploadingExtra || extraMedia.length >= 6}
                  data-testid="add-sighting-link-add-photo"
                  className="mt-2 inline-flex items-center gap-1 rounded-lg border border-dashed border-black/25 bg-[color:var(--np-bg,#F5EBDC)]/50 hover:bg-[color:var(--np-bg,#F5EBDC)] px-3 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#1B4332] disabled:opacity-50"
                >
                  <Plus size={12} weight="bold" />
                  {uploadingExtra ? "Uploading…" : extraMedia.length ? "Add more photos" : "Add supplementary photos"}
                </button>
                <input
                  ref={extraFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={onExtraFiles}
                  className="hidden"
                  data-testid="add-sighting-link-photo-input"
                />
              </div>

              {/* Submit */}
              <div className="pt-2 flex items-center gap-3">
                <button
                  disabled={submitting || expanding || !linkPreview.headline?.trim()}
                  onClick={submitLink}
                  data-testid="add-sighting-link-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                >
                  <CheckCircle size={13} weight="fill" /> {submitting ? "Posting…" : "Post link story"}
                </button>
                <button
                  type="button"
                  onClick={clear}
                  className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#1B4332]"
                >Cancel</button>
              </div>
              {!user && (
                <div className="text-xs text-[#3A3830]">
                  Sign-in required — the Observation Network keeps every link attributable so followers and curators can act on it.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right column: text-first story CTA (hidden once a photo or link has been picked) */}
        {emptyMode && (
          <aside
            className="rounded-2xl bg-[#FBF3E4] text-[#241209] border border-[#E4A15C]/40 p-6 md:p-7 shadow-sm flex flex-col"
            data-testid="tell-story-panel"
          >
            <div className="mb-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#B85F2A]/12 grid place-items-center text-[#B85F2A]">
                <Notebook size={16} weight="fill" />
              </div>
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#B85F2A]">
                No photo? No problem.
              </div>
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight text-[#1B4332]">
              Tell a story.
            </h3>
            <p className="mt-2 text-sm md:text-base text-[#3A3830]">
              A memory, an observation, a question, a research link, a warning — anything about nature that matters. Words are enough.
            </p>

            <ul className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              {[
                { Icon: Article, label: "Memory" },
                { Icon: Question, label: "Question" },
                { Icon: WarningCircle, label: "Warning" },
                { Icon: Sparkle, label: "Research" },
              ].map(({ Icon, label }) => (
                <li key={label} className="inline-flex items-center gap-1.5 rounded-full border border-[#B85F2A]/25 bg-white/70 px-2.5 py-1 font-mono-tech tracking-[0.22em] uppercase text-[#5B4A38]">
                  <Icon size={11} weight="fill" className="text-[#B85F2A]" /> {label}
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/stories/new"
                data-testid="tell-story-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#a25526]"
              >
                Share a story <ArrowRight size={12} weight="bold" />
              </Link>
              <Link
                to="/stories"
                className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#5B4A38] hover:text-[#1B4332]"
              >
                Browse the archive →
              </Link>
            </div>
          </aside>
        )}
        </div>
      </div>
    </section>
  );
}
